import React from 'react';
import { Receipt, CreditCard, Percent, Home, MapPin, Calendar, Users, Award } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета налога на имущество
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Получаем имя типа имущества
  const getPropertyTypeName = () => {
    return results.debugInfo.propertyTypeName || 'Имущество';
  };

  return (
    <ToolResult title="Результаты расчета налога на имущество">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Итоговая сумма налога */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Receipt className="w-4 h-4"/>
            Сумма налога к уплате
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.finalTax, 0)}
            </span>
            <span className="text-sm text-indigo-600 dark:text-indigo-400">
              за {results.taxYear} год
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {results.regionName}, {getPropertyTypeName().toLowerCase()}
          </p>
        </div>

        {/* Налоговая ставка и основная информация */}
        <div className="rounded-lg bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 p-5 border border-orange-100 dark:border-orange-800/50 shadow-sm">
          <h3 className="text-base font-medium text-orange-700 dark:text-orange-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Налоговая ставка
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-orange-800 dark:text-orange-200" aria-live="polite">
              {results.taxRate}%
            </span>
            <span className="text-sm text-orange-600 dark:text-orange-400">
              от кадастровой стоимости
            </span>
          </div>
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
            Базовый налог до льгот и вычетов: {formatCurrency(results.baseTax, 0)}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Кадастровая стоимость" 
          value={formatCurrency(results.cadastralValue, 0)} 
          color="indigo" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип имущества" 
          value={getPropertyTypeName()} 
          color="blue" 
          icon={<Home className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Регион" 
          value={results.regionName} 
          color="teal" 
          icon={<MapPin className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Налоговый период" 
          value={results.taxYear.toString()} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Площадь объекта" 
          value={`${formatNumber(results.area, 1)} кв.м.`} 
          color="green"
        />

        {results.deduction > 0 && (
          <ResultItem 
            label="Налоговый вычет" 
            value={`${results.deductionArea} кв.м. (${formatCurrency(results.deduction, 0)})`}
            color="green"
          />
        )}

        {results.reducingCoefficientValue < 1 && (
          <ResultItem 
            label="Понижающий коэффициент" 
            value={results.reducingCoefficientValue.toString()} 
            color="purple"
          />
        )}
        
        {results.ownershipShareFactor < 1 && (
          <ResultItem 
            label="Доля в собственности" 
            value={`${(results.ownershipShareFactor * 100).toFixed(0)}%`} 
            color="orange" 
            icon={<Users className="w-4 h-4"/>}
          />
        )}
        
        {results.ownershipPeriodFactor < 1 && (
          <ResultItem 
            label="Период владения" 
            value={`${Math.round(results.ownershipPeriodFactor * 12)} мес. (${(results.ownershipPeriodFactor * 100).toFixed(0)}% года)`} 
            color="orange" 
          />
        )}
        
        {results.privilegeDiscount > 0 && (
          <ResultItem 
            label="Налоговая льгота" 
            value={`${formatCurrency(results.privilegeDiscount, 0)} (${results.privilegeDescription})`} 
            color="green" 
            icon={<Award className="w-4 h-4"/>}
          />
        )}
      </div>

      {/* Блок с информацией о сроках уплаты */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mt-4 border border-blue-200 dark:border-blue-700">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Сроки уплаты налога на имущество</h4>
        <p className="text-sm text-blue-700 dark:text-blue-400">
          Налог на имущество физических лиц за {results.taxYear} год необходимо уплатить до <span className="font-semibold">1 декабря {results.taxYear + 1} года</span>. 
          Налоговое уведомление придет по почте или в личный кабинет налогоплательщика на сайте ФНС.
        </p>
      </div>

      {/* Дисклеймер */}
      <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Результаты расчета являются приблизительными и могут отличаться от суммы в налоговом уведомлении. 
          Точную информацию о налоге можно получить в налоговой инспекции или на сайте ФНС России.
        </p>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;